import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { checkMediaHealth, countMedia, deleteMedia, listMedia, MediaItem, uploadMedia, saveMediaMeta } from '../lib/media';
import Toast from '../components/Toast';
import ImageCompressModal from '../components/ImageCompressModal';
import ImageLightbox from '../components/ImageLightbox';
import ImageMetadataModal from '../components/ImageMetadataModal';
import ComponentSettingsModal from '../components/ComponentSettingsModal';
import { useUIStore } from '../store/uiStore';

const cls = {
  btn: 'inline-flex items-center justify-center rounded-lg px-3 py-2 text-sm font-medium transition-colors',
  primary: 'bg-gray-900 text-white hover:bg-gray-800',
  ghost: 'border border-gray-200 hover:bg-gray-50',
  input: 'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-purple-500',
  tile: 'rounded-xl border border-gray-200 bg-white shadow-sm',
};

const humanSize = (n: number) => {
  const u = ['B','KB','MB','GB'];
  let i = 0; let v = n;
  while (v >= 1024 && i < u.length-1) { v /= 1024; i++; }
  return `${v.toFixed(1)} ${u[i]}`;
};

const MediaGallery: React.FC = () => {
  const chromeHidden = !!useUIStore((s) => (s as any).chromeHidden);
  const setChromeHidden = useUIStore((s) => (s as any).setChromeHidden);
  const [items, setItems] = useState<MediaItem[]>([]);
  const [fastCount, setFastCount] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<MediaItem | null>(null);
  const [uploading, setUploading] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success'|'error'|'info' }|null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [checking, setChecking] = useState(false);
  const CLIENT_MAX = 50 * 1024 * 1024;
  const [compressEnabled, setCompressEnabled] = useState<boolean>(() => {
    try {
      const v = localStorage.getItem('compressEnabled');
      return v ? v === '1' : true; // default ON so the compression popup shows
    } catch { return true; }
  });
  const [modalOpen, setModalOpen] = useState(false);
  const [modalFile, setModalFile] = useState<File | null>(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState<number>(0);
  const [metaModalOpen, setMetaModalOpen] = useState(false);
  const [pendingMetaFor, setPendingMetaFor] = useState<MediaItem | null>(null);
  const [meta, setMeta] = useState<Record<string, { tags: string[]; description: string }>>({});
  const [componentPreviewOpen, setComponentPreviewOpen] = useState(false);
  const [prefabText, setPrefabText] = useState<string | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settingsUrl, setSettingsUrl] = useState<string | null>(null);
  const [settingsInitial, setSettingsInitial] = useState<Partial<{ text: string; fontSize: number; textColor: string; bgColor: string; radius: number; padding: number }> | null>(null);

  const linkedComponentRel = useMemo(() => {
    try {
      if (!selected || !isPrefab(selected) || !prefabText) return null;
      const j = JSON.parse(prefabText);
      return (j && typeof j.component === 'string') ? String(j.component).replace(/^\/+/, '') : null;
    } catch { return null; }
  }, [selected, prefabText]);

  const lightboxItems = useMemo(() => items.map(it => ({ url: it.url, name: it.name })), [items]);

  useEffect(() => {
    return () => {
      try { setChromeHidden(false); } catch {}
    };
  }, [setChromeHidden]);

  useEffect(() => {
    try { localStorage.setItem('compressEnabled', compressEnabled ? '1' : '0'); } catch {}
  }, [compressEnabled]);

  const refresh = async () => {
    setLoading(true);
    setError(null);
    try {
      // kick off count in parallel
      countMedia().then(c => setFastCount(c)).catch(()=>{});
      const list = await listMedia();
      const sorted = list.sort((a,b) => b.mtime - a.mtime);
      setItems(sorted);
      // Build meta map from media.json so Details shows saved data
      const metaMap: Record<string, { tags: string[]; description: string }> = {};
      for (const it of sorted) {
        const tags = Array.isArray((it as any).tags) ? (it as any).tags.filter(Boolean) as string[] : [];
        const desc = typeof (it as any).description === 'string' ? (it as any).description : '';
        if (tags.length || desc) metaMap[it.name] = { tags, description: desc };
      }
      setMeta(metaMap);
    } catch (e: any) {
      setError(e?.message || 'Failed to load media');
    } finally {
      setLoading(false);
    }
  };

  const isJs = (it: MediaItem | null | undefined) => !!it && (it.name.toLowerCase().endsWith('.js') || ((it.mime || '').includes('javascript')));
  const isJsx = (it: MediaItem | null | undefined) => !!it && it.name.toLowerCase().endsWith('.jsx');
  const isImage = (it: MediaItem | null | undefined) => !!it && (
    (it.mime || '').startsWith('image/') ||
    ['.jpg','.jpeg','.png','.gif','.webp','.svg','.bmp','.tiff','.ico','.heif','.heic'].some(ext => it.name.toLowerCase().endsWith(ext))
  );
  const isPrefab = (it: MediaItem | null | undefined) => !!it && it.name.toLowerCase().endsWith('.json');
  const imageExt = (it: MediaItem): string => {
    const n = (it.name || '').toLowerCase();
    const m = (it.mime || '').toLowerCase();
    if (n.endsWith('.svg') || m === 'image/svg+xml') return 'svg';
    if (n.endsWith('.jpeg') || m === 'image/jpeg') return 'jpeg';
    if (n.endsWith('.jpg')) return 'jpg';
    if (n.endsWith('.png') || m === 'image/png' || m === 'image/x-png') return 'png';
    if (n.endsWith('.gif') || m === 'image/gif') return 'gif';
    if (n.endsWith('.webp') || m === 'image/webp') return 'webp';
    if (n.endsWith('.bmp') || m === 'image/bmp') return 'bmp';
    if (n.endsWith('.tiff') || m === 'image/tiff') return 'tiff';
    if (n.endsWith('.ico') || m === 'image/x-icon') return 'ico';
    if (n.endsWith('.heif') || m === 'image/heif') return 'heif';
    if (n.endsWith('.heic') || m === 'image/heic') return 'heic';
    const mm = m.startsWith('image/') ? m.replace('image/','') : '';
    return mm || (n.split('.').pop() || '');
  };
  const imageFilterKeys = ['all','svg','png','jpg','jpeg','gif','webp','bmp','tiff','ico','heif','heic'] as const;
  const [imageFilter, setImageFilter] = useState<string>('all');
  const imageCounts = useMemo(() => {
    const map: Record<string, number> = { all: 0 };
    for (const k of imageFilterKeys as readonly string[]) map[k] = map[k] || 0;
    for (const it of items) {
      if (!isImage(it)) continue;
      const ext = imageExt(it);
      map.all++;
      if (ext) map[ext] = (map[ext] || 0) + 1;
    }
    return map;
  }, [items]);

  useEffect(() => { refresh(); }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!selected || !isPrefab(selected)) { setPrefabText(null); return; }
      try {
        const res = await fetch(selected.url, { cache: 'no-store' });
        if (!res.ok) throw new Error('Failed to load prefab');
        const txt = await res.text();
        if (alive) setPrefabText(txt);
      } catch {
        if (alive) setPrefabText('');
      }
    })();
    return () => { alive = false; };
  }, [selected]);

  const afterUpload = (uploaded: MediaItem) => {
    setItems(prev => [uploaded, ...prev]);
    setPendingMetaFor(uploaded);
    setTimeout(() => setMetaModalOpen(true), 0);
  };

  const onUploadClick = () => inputRef.current?.click();

  const onFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > CLIENT_MAX) {
      setToast({ message: 'File too large for client limit (50MB).', type: 'error' });
      e.target.value = '';
      return;
    }
    const allowed = ['image/jpeg','image/png','image/gif','image/webp','image/svg+xml','image/bmp','image/tiff','image/x-icon','image/heif','image/heic'];
    if (file.type && !allowed.includes(file.type)) {
      setToast({ message: `Unsupported type: ${file.type || 'unknown'}`, type: 'error' });
      e.target.value = '';
      return;
    }
    // Bypass compression for SVGs to preserve vector data
    if (file.type === 'image/svg+xml' || file.name.toLowerCase().endsWith('.svg')) {
      setUploading(true);
      try {
        const uploaded = await uploadMedia(file);
        afterUpload(uploaded);
        setToast({ message: 'Upload successful', type: 'success' });
      } catch (e: any) {
        setToast({ message: e?.message || 'Upload failed', type: 'error' });
      }
      setUploading(false);
      e.target.value = '';
      return;
    }

    if (compressEnabled) {
      setModalFile(file);
      setModalOpen(true);
      e.target.value = '';
      return;
    }
    setUploading(true);
    try {
      const uploaded = await uploadMedia(file);
      afterUpload(uploaded);
      setToast({ message: 'Upload successful', type: 'success' });
    } catch (e: any) {
      setToast({ message: e?.message || 'Upload failed', type: 'error' });
    }
    setUploading(false);
    e.target.value = '';
  };

  const onStatus = async () => {
    setChecking(true);
    try {
      const h = await checkMediaHealth();
      if (!h) { setToast({ message: 'No PHP server reachable', type: 'error' }); return; }
      const i = h.info;
      const msg = `Server ${h.base}: uploads(${i.uploadsExists ? 'exists' : 'missing'}/${i.uploadsWritable ? 'w' : 'ro'}), media.json(${i.mediaJsonExists ? 'exists' : 'missing'}/${i.mediaJsonWritable ? 'w' : 'ro'}), php limits: upload_max_filesize=${i.php?.upload_max_filesize}, post_max_size=${i.php?.post_max_size}`;
      setToast({ message: msg, type: 'info' });
    } catch (e: any) {
      setToast({ message: e?.message || 'Health check failed', type: 'error' });
    }
    setChecking(false);
  };

  const onDelete = async (it: MediaItem) => {
    const ok = await deleteMedia(it.name);
    if (ok) {
      setItems(prev => prev.filter(x => x.name !== it.name));
      if (selected?.name === it.name) setSelected(null);
      setToast({ message: 'Deleted media', type: 'success' });
    } else {
      setToast({ message: 'Failed to delete media', type: 'error' });
    }
  };

  return (
    <>
    <div className="min-h-screen bg-gray-50 lg:overflow-hidden">
      {toast && (
        <div className="fixed top-4 right-4 z-50 pointer-events-none">
          <div className="pointer-events-auto">
            <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
          </div>
        </div>
      )}
      <header className="sticky top-0 z-30 bg-white/70 backdrop-blur border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Link
              to="/admin"
              className="inline-flex items-center gap-2 h-9 px-3 rounded-lg border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 hover:border-gray-300 shadow-sm"
              title="Back to Admin"
            >
              <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M15 18l-6-6 6-6" />
              </svg>
              <span className="hidden sm:inline">Back</span>
            </Link>
            <span className="font-semibold text-gray-900">Media Gallery</span>
            <button
              type="button"
              onClick={() => setChromeHidden(!chromeHidden)}
              className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 hover:border-gray-300 shadow-sm transition"
              title={chromeHidden ? 'Show header and navigation bar' : 'Hide header and navigation bar'}
              aria-label={chromeHidden ? 'Show header and navigation bar' : 'Hide header and navigation bar'}
            >
              {chromeHidden ? (
                <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M6 9l6 6 6-6" />
                </svg>
              ) : (
                <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 15l-6-6-6 6" />
                </svg>
              )}
            </button>
            <span className="ml-2 inline-flex items-center gap-2 rounded-md border border-gray-200 px-2 py-0.5 text-xs text-gray-700 bg-white">
              {fastCount === null && loading ? (
                <>
                  <span className="h-3.5 w-3.5 rounded-full border-2 border-gray-300 border-t-purple-600 animate-spin" />
                  <span>Detecting...</span>
                </>
              ) : (
                <>
                  <span className="h-2 w-2 rounded-full bg-green-500" />
                  <span>Detected: {fastCount ?? items.length} files</span>
                </>
              )}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <input ref={inputRef} type="file" accept="image/*,.svg,image/svg+xml" className="hidden" onChange={onFileChange} />
            <button className={`${cls.btn} ${cls.ghost}`} onClick={refresh}>{loading ? 'Refreshing...' : 'Refresh'}</button>
            <button className={`${cls.btn} ${cls.ghost}`} onClick={onStatus} disabled={checking}>{checking ? 'Checking...' : 'Status'}</button>
            <button className={`${cls.btn} ${cls.primary}`} onClick={onUploadClick} disabled={uploading}>{uploading ? 'Uploading...' : 'Upload'}</button>
            <label className="ml-2 inline-flex items-center gap-2 text-sm text-gray-700">
              <input type="checkbox" className="h-4 w-4" checked={compressEnabled} onChange={e => setCompressEnabled(e.target.checked)} />
              Compress before upload
            </label>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-6 lg:h-[calc(100vh-56px)] lg:overflow-hidden lg:min-h-0 overscroll-none">
        <div className="space-y-6 pt-6 pb-6 lg:h-full lg:overflow-y-auto lg:min-h-0">
        <section className={`${cls.tile} p-4`}>
          <div className="mb-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="h-9 w-9 inline-flex items-center justify-center rounded-full bg-purple-50">
                <svg className="h-5 w-5 text-purple-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="5" width="18" height="14" rx="2"/><path d="M3 15l4-4 4 4 5-5 4 4"/></svg>
              </span>
              <h2 className="font-semibold text-gray-900">Fotos / Graphics</h2>
            </div>
            <div className="flex items-center gap-1 overflow-x-auto max-w-full pl-2">
              {(['all', ...imageFilterKeys.filter(k=>k!=='all')] as string[]).map((key) => (
                <button
                  key={key}
                  onClick={() => setImageFilter(key)}
                  className={
                    `inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs whitespace-nowrap transition ` +
                    (imageFilter === key
                      ? 'bg-purple-600 border-purple-600 text-white shadow-sm'
                      : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50')
                  }
                >
                  <span className="capitalize">{key}</span>
                  <span className={
                    `ml-1 inline-flex h-4 min-w-[1rem] items-center justify-center rounded-full px-1 ` +
                    (imageFilter === key ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-700')
                  }>{imageCounts[key] ?? 0}</span>
                </button>
              ))}
            </div>
          </div>
          {error && (
            <div className="mb-3 rounded-lg border border-red-200 bg-red-50 text-red-800 px-3 py-2 text-sm">
              {error}
            </div>
          )}
          {loading ? (
            <div className="flex items-center justify-center h-48 text-gray-700">
              <span className="h-6 w-6 rounded-full border-2 border-gray-300 border-t-purple-600 animate-spin mr-2" />
              <span>Loading media...</span>
            </div>
          ) : items.length === 0 ? (
            <div className="text-gray-600">No media yet. Upload your first image.</div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {items
                .filter(it => isImage(it))
                .filter(it => imageFilter === 'all' ? true : imageExt(it) === imageFilter)
                .map((it) => (
                <button key={it.name} onClick={() => setSelected(it)} className="group aspect-square rounded-lg overflow-hidden border border-gray-200 bg-gray-100">
                  <img src={it.url} alt={it.name} className="h-full w-full object-cover group-hover:scale-105 transition-transform" />
                </button>
              ))}
            </div>
          )}
        </section>
        
        <section className={`${cls.tile} p-4`}>
          <div className="mb-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="h-9 w-9 inline-flex items-center justify-center rounded-full bg-blue-50">
                <svg className="h-5 w-5 text-blue-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="5" width="18" height="14" rx="2"/><path d="M10 9l5 3-5 3V9z"/></svg>
              </span>
              <h2 className="font-semibold text-gray-900">Videos</h2>
            </div>
            <span className="inline-flex items-center gap-2 rounded-md border border-blue-200 bg-blue-50 px-2 py-0.5 text-xs text-blue-700">Coming soon</span>
          </div>
          <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-8 text-center text-gray-600">
            Video upload and browsing will be available here.
          </div>
        </section>

        <section className={`${cls.tile} p-4`}>
          <div className="mb-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="h-9 w-9 inline-flex items-center justify-center rounded-full bg-emerald-50">
                <svg className="h-5 w-5 text-emerald-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87L18.18 22 12 18.77 5.82 22 7 14.14l-5-4.87 6.91-1.01L12 2z"/></svg>
              </span>
              <h2 className="font-semibold text-gray-900">SVGs</h2>
            </div>
            <span className="inline-flex items-center gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-xs text-emerald-700">Vector</span>
          </div>
          {(() => { const svgs = items.filter(it => (it.name||'').toLowerCase().endsWith('.svg') || (it.mime||'') === 'image/svg+xml'); return (
            svgs.length === 0 ? (
              <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-8 text-center text-gray-600">
                No SVGs yet. Upload SVG files to manage them here.
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                {svgs.map((it) => (
                  <button key={it.name} onClick={() => setSelected(it)} className="group aspect-square rounded-lg overflow-hidden border border-gray-200 bg-white hover:ring-2 hover:ring-emerald-300">
                    <img src={it.url} alt={it.name} className="h-full w-full object-contain p-2 group-hover:scale-105 transition-transform" />
                  </button>
                ))}
              </div>
            )
          ); })()}
        </section>

        <section className={`${cls.tile} p-4`}>
          <div className="mb-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="h-9 w-9 inline-flex items-center justify-center rounded-full bg-amber-50">
                <svg className="h-5 w-5 text-amber-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2l7 4v8l-7 4-7-4V6l7-4z"/><path d="M12 22V12"/></svg>
              </span>
              <h2 className="font-semibold text-gray-900">Vector Graphics</h2>
            </div>
            <span className="inline-flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50 px-2 py-0.5 text-xs text-amber-700">Coming soon</span>
          </div>
          <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-8 text-center text-gray-600">
            Upload and organize vector files like AI, EPS, PDF.
          </div>
        </section>

        <section className={`${cls.tile} p-4`}>
          <div className="mb-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="h-9 w-9 inline-flex items-center justify-center rounded-full bg-pink-50">
                <svg className="h-5 w-5 text-pink-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 19h16"/><path d="M9 8h6"/><path d="M8 12h8"/><path d="M6 16h12"/></svg>
              </span>
              <h2 className="font-semibold text-gray-900">Fonts</h2>
            </div>
            <span className="inline-flex items-center gap-2 rounded-md border border-pink-200 bg-pink-50 px-2 py-0.5 text-xs text-pink-700">Coming soon</span>
          </div>
          <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-8 text-center text-gray-600">
            Upload and preview custom fonts.
          </div>
        </section>

        <section className={`${cls.tile} p-4`}>
          <div className="mb-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="h-9 w-9 inline-flex items-center justify-center rounded-full bg-sky-50">
                <svg className="h-5 w-5 text-sky-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 4h16v6H4z"/><path d="M4 14h16v6H4z"/><path d="M8 7h8"/><path d="M8 17h8"/></svg>
              </span>
              <h2 className="font-semibold text-gray-900">Components (Javascript)</h2>
            </div>
            <span className="inline-flex items-center gap-2 rounded-md border border-sky-200 bg-sky-50 px-2 py-0.5 text-xs text-sky-700">JS</span>
          </div>
          {(() => { const comps = items.filter(it => isJs(it)); return (
            comps.length === 0 ? (
              <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-8 text-center text-gray-600">
                No JavaScript components yet.
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                {comps.map((it) => { const nameKey = (it.name || '').toLowerCase(); const highlight = !!linkedComponentRel && nameKey === linkedComponentRel.toLowerCase(); return (
                  <button key={it.name} onClick={() => setSelected(it)} aria-current={highlight ? 'true' : undefined} className={"group aspect-square rounded-lg overflow-hidden border bg-yellow-50 hover:ring-2 hover:ring-yellow-300 " + (highlight ? "border-purple-400 ring-2 ring-purple-400" : "border-gray-200")}>
                    <div className="h-full w-full flex flex-col items-center justify-center p-3">
                      <div className="h-12 w-12 rounded-md bg-yellow-400 text-black font-extrabold flex items-center justify-center shadow-sm">JS</div>
                      <div className="mt-2 text-xs text-gray-800 text-center line-clamp-2">{it.name}</div>
                    </div>
                  </button>
                ); })}
              </div>
            )
          ); })()}
        </section>

        <section className={`${cls.tile} p-4`}>
          <div className="mb-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="h-9 w-9 inline-flex items-center justify-center rounded-full bg-teal-50">
                <svg className="h-5 w-5 text-teal-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M4.5 12h15"/><path d="M12 4.5v15"/></svg>
              </span>
              <h2 className="font-semibold text-gray-900">React Components (Javascript XML/ JSX)</h2>
            </div>
            <span className="inline-flex items-center gap-2 rounded-md border border-teal-200 bg-teal-50 px-2 py-0.5 text-xs text-teal-700">JSX</span>
          </div>
          {(() => { const comps = items.filter(it => isJsx(it)); return (
            comps.length === 0 ? (
              <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-8 text-center text-gray-600">
                No React JSX components yet.
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                {comps.map((it) => { const nameKey = (it.name || '').toLowerCase(); const highlight = !!linkedComponentRel && nameKey === linkedComponentRel.toLowerCase(); return (
                  <button key={it.name} onClick={() => setSelected(it)} aria-current={highlight ? 'true' : undefined} className={"group aspect-square rounded-lg overflow-hidden border bg-teal-50 hover:ring-2 hover:ring-teal-300 " + (highlight ? "border-purple-400 ring-2 ring-purple-400" : "border-gray-200")}>
                    <div className="h-full w-full flex flex-col items-center justify-center p-3">
                      <div className="h-12 w-12 rounded-md bg-teal-500 text-white font-extrabold flex items-center justify-center shadow-sm">JSX</div>
                      <div className="mt-2 text-xs text-gray-800 text-center line-clamp-2">{it.name}</div>
                    </div>
                  </button>
                ); })}
              </div>
            )
          ); })()}
        </section>

        <section className={`${cls.tile} p-4`}>
          <div className="mb-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="h-9 w-9 inline-flex items-center justify-center rounded-full bg-purple-50">
                <svg className="h-5 w-5 text-purple-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="16" rx="2"/><path d="M7 8h10"/><path d="M7 12h6"/><path d="M7 16h3"/></svg>
              </span>
              <h2 className="font-semibold text-gray-900">Prefabs</h2>
            </div>
            <span className="inline-flex items-center gap-2 rounded-md border border-purple-200 bg-purple-50 px-2 py-0.5 text-xs text-purple-700">JSON</span>
          </div>
          {(() => { const prefabs = items.filter(it => isPrefab(it)); return (
            prefabs.length === 0 ? (
              <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-8 text-center text-gray-600">
                No prefabs yet.
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                {prefabs.map((it) => (
                  <button key={it.name} onClick={() => setSelected(it)} className="group aspect-square rounded-lg overflow-hidden border border-gray-200 bg-purple-50 hover:ring-2 hover:ring-purple-300">
                    <div className="h-full w-full flex flex-col items-center justify-center p-3">
                      <div className="h-12 w-12 rounded-md bg-purple-500 text-white font-extrabold flex items-center justify-center shadow-sm">{`{}`}</div>
                      <div className="mt-2 text-xs text-gray-800 text-center line-clamp-2">{it.name}</div>
                    </div>
                  </button>
                ))}
              </div>
            )
          ); })()}
        </section>
        </div>

        <aside className={`${cls.tile} p-4 lg:h-full lg:overflow-y-auto pt-6 pb-6 lg:min-h-0 overscroll-contain`}>
          <div className="font-semibold text-gray-900 mb-2">Details</div>
          {!selected ? (
            <div className="text-gray-600">Select a media item to view details.</div>
          ) : (
            <div className="space-y-3">
              {isJs(selected) ? (
                <div className="relative rounded-lg overflow-hidden border border-gray-200 bg-white">
                  <button
                    type="button"
                    title="Open fullscreen preview"
                    aria-label="Open fullscreen preview"
                    className="absolute top-2 right-2 z-10 inline-flex h-8 w-8 items-center justify-center rounded-md bg-white/80 backdrop-blur border border-gray-200 shadow-sm hover:bg-white hover:shadow transition"
                    onClick={() => setComponentPreviewOpen(true)}
                  >
                    <svg className="h-4 w-4 text-gray-700" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M15 3h6v6"/><path d="M9 21H3v-6"/><path d="M21 3l-7 7"/><path d="M3 21l7-7"/></svg>
                  </button>
                  <button
                    type="button"
                    title="Component settings"
                    aria-label="Component settings"
                    className="absolute top-2 right-12 z-10 inline-flex h-8 w-8 items-center justify-center rounded-md bg-white/80 backdrop-blur border border-gray-200 shadow-sm hover:bg-white hover:shadow transition"
                    onClick={() => { setSettingsInitial(null); setSettingsUrl(selected.url); setSettingsOpen(true); }}
                  >
                    <svg className="h-4 w-4 text-gray-700" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 15.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7Z"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9c0 .66.39 1.26 1 1.51H21a2 2 0 1 1 0 4h-.09c-.6.25-1 .85-1 1.49Z"/></svg>
                  </button>
                  <iframe
                    title="Component Preview"
                    className="w-full h-48 bg-white"
                    sandbox="allow-scripts allow-modals"
                    srcDoc={`<!doctype html><html><head><meta charset=\"utf-8\"/><meta name=\"viewport\" content=\"width=device-width,initial-scale=1\"/><style>html,body{margin:0;padding:0;background:#fff;font-family:ui-sans-serif,system-ui,-apple-system,Segoe UI,Roboto,Ubuntu,Cantarell,Noto Sans,sans-serif}#app{display:flex;align-items:center;justify-content:center;min-height:12rem;padding:12px}</style></head><body><div id=\"app\"></div><script>window.__COMPONENT_TARGET__=document.getElementById('app');</script><script src=\"${(selected.url || '').replace(/"/g,'&quot;')}\"></script></body></html>`}
                  />
                </div>
              ) : isPrefab(selected) ? (
                <div className="relative rounded-lg overflow-hidden border border-gray-200 bg-white">
                  <div className="px-3 py-2 text-xs uppercase tracking-wide text-purple-700 bg-purple-50 border-b border-purple-100">Prefab Preview</div>
                  {(() => { try { const j = prefabText ? JSON.parse(prefabText) : null; const compRel = j && typeof j.component === 'string' ? String(j.component).replace(/^\/+/, '') : null; const compPath = compRel ? '/media.php?name=' + compRel : null; return compPath ? (
                    <button
                      type="button"
                      title="Component settings"
                      aria-label="Component settings"
                      className="absolute top-2 right-2 z-10 inline-flex h-8 w-8 items-center justify-center rounded-md bg-white/80 backdrop-blur border border-gray-200 shadow-sm hover:bg-white hover:shadow transition"
                      onClick={() => { const init = (j && j.props && typeof j.props === 'object') ? j.props : null; setSettingsInitial(init); setSettingsUrl(compPath); setSettingsOpen(true); }}
                    >
                      <svg className="h-4 w-4 text-gray-700" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 15.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7Z"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9c0 .66.39 1.26 1 1.51H21a2 2 0 1 1 0 4h-.09c-.6.25-1 .85-1 1.49Z"/></svg>
                    </button>
                  ) : null; } catch { return null; } })()}
                  <div className="p-0">
                    {(() => {
                      try {
                        const j = prefabText ? JSON.parse(prefabText) : null;
                        const compRel = j && typeof j.component === 'string' ? String(j.component).replace(/^\/+/, '') : null;
                        const compPath = compRel ? '/media.php?name=' + compRel : null;
                        if (!compPath) return <div className="p-3 text-sm text-gray-600">Invalid prefab: missing component path.</div>;
                        const props = (j && j.props) ? j.props : {};
                        const propsJson = JSON.stringify(props).replace(/<\//g, '<\\/');
                        return (
                          <iframe
                            title="Prefab Component Preview"
                            className="w-full h-48 bg-white"
                            sandbox="allow-scripts allow-modals"
                            srcDoc={`<!doctype html><html><head><meta charset=\"utf-8\"/><meta name=\"viewport\" content=\"width=device-width,initial-scale=1\"/><style>html,body{margin:0;padding:0;background:#fff;font-family:ui-sans-serif,system-ui,-apple-system,Segoe UI,Roboto,Ubuntu,Cantarell,Noto Sans,sans-serif}#app{display:flex;align-items:center;justify-content:center;min-height:12rem;padding:12px}</style></head><body><div id=\"app\"></div><script>window.__COMPONENT_TARGET__=document.getElementById('app');window.__PREFAB_PROPS__=${propsJson};(function(){var tries=0;var t=setInterval(function(){tries++;try{if(window.__COMPONENT_SET_PROPS){window.__COMPONENT_SET_PROPS(window.__PREFAB_PROPS__);clearInterval(t);} }catch(e){} if(tries>30){clearInterval(t);}},100); setTimeout(function(){try{if(window.__COMPONENT_SET_PROPS){window.__COMPONENT_SET_PROPS(window.__PREFAB_PROPS__);} }catch(e){}},300);})();</script><script src=\"${compPath.replace(/"/g,'&quot;')}\"></script></body></html>`}
                          />
                        );
                      } catch {
                        return <div className="p-3 text-sm text-red-600">Failed to parse prefab JSON.</div>;
                      }
                    })()}
                  </div>
                  {(() => { try { const j = prefabText ? JSON.parse(prefabText) : null; const rel = j && typeof j.component === 'string' ? String(j.component).replace(/^\/+/, '') : null; const compUrl = rel ? '/media.php?name=' + rel : null; return rel ? (
                    <div className="px-3 py-2 text-xs text-gray-700 bg-purple-50 border-t border-purple-100 flex items-center justify-between">
                      <div>
                        <span className="font-medium text-purple-700">Component:</span> <span className="text-gray-900">{rel}</span>
                      </div>
                      {compUrl && (
                        <button className="inline-flex items-center gap-1 rounded-md border border-gray-200 bg-white px-2 py-1 text-xs text-gray-800 hover:bg-gray-50" onClick={() => { setSettingsInitial(null); setSettingsUrl(compUrl); setSettingsOpen(true); }}>
                          <svg className="h-3.5 w-3.5 text-gray-700" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 15.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7Z"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9c0 .66.39 1.26 1 1.51H21a2 2 0 1 1 0 4h-.09c-.6.25-1 .85-1 1.49Z"/></svg>
                          Edit Component
                        </button>
                      )}
                    </div>
                  ) : null; } catch { return null; } })()}
                  <div className="px-3 py-2 text-xs uppercase tracking-wide text-purple-700 bg-purple-50 border-t border-purple-100">JSON</div>
                  <pre className="m-0 max-h-64 overflow-auto p-3 text-xs text-gray-800"><code>{prefabText ?? 'Loading...'}</code></pre>
                </div>
              ) : (
                <button
                  className="rounded-lg overflow-hidden border border-gray-200 bg-gray-100 cursor-zoom-in"
                  onClick={() => { const i = items.findIndex(x => x.name === selected.name); if (i >= 0) { setLightboxIndex(i); setLightboxOpen(true); } }}
                >
                  <img src={selected.url} alt={selected.name} className="w-full object-cover" />
                </button>
              )}
              <div className="text-sm text-gray-700"><span className="font-medium">Name:</span> {selected.name}</div>
              <div className="text-sm text-gray-700"><span className="font-medium">Size:</span> {humanSize(selected.size)}</div>
              <div className="text-sm text-gray-700"><span className="font-medium">Updated:</span> {new Date(selected.mtime * 1000).toLocaleString()}</div>
              <div className="text-sm text-gray-700"><span className="font-medium">Tags:</span> {(meta[selected.name]?.tags?.length ? meta[selected.name].tags.join(', ') : '— add tags —')}</div>
              <div className="text-sm text-gray-700"><span className="font-medium">AI Meta Description:</span> {meta[selected.name]?.description ? meta[selected.name].description : '— add description —'}</div>
              <div className="flex gap-2 pt-2">
                <a href={selected.url} target="_blank" rel="noreferrer" className={`${cls.btn} ${cls.ghost}`}>Open</a>
                <button
                  className={`${cls.btn} ${cls.ghost}`}
                  onClick={() => {
                    const u = selected.url.startsWith('http') ? selected.url : (window.location.origin + selected.url);
                    navigator.clipboard.writeText(u);
                  }}
                >Copy URL</button>
                <button className={`${cls.btn} ${cls.ghost}`} onClick={() => { setPendingMetaFor(selected); setMetaModalOpen(true); }}>Edit metadata</button>
                <button className={`${cls.btn} ${cls.ghost}`} onClick={() => onDelete(selected)}>Delete</button>
              </div>
            </div>
          )}
        </aside>
      </div>
    </div>
    <ImageCompressModal
      open={modalOpen}
      file={modalFile}
      onClose={() => { setModalOpen(false); setModalFile(null); }}
      onConfirm={async (processed) => {
        setModalOpen(false);
        setUploading(true);
        try {
          const uploaded = await uploadMedia(processed);
          afterUpload(uploaded);
          setToast({ message: 'Upload successful', type: 'success' });
        } catch (e: any) {
          setToast({ message: e?.message || 'Upload failed', type: 'error' });
        }
        setUploading(false);
        setModalFile(null);
      }}
    />
    <ImageMetadataModal
      open={metaModalOpen}
      onClose={() => { setMetaModalOpen(false); setPendingMetaFor(null); }}
      onConfirm={async (data) => {
        const target = pendingMetaFor?.name || selected?.name;
        if (!target) { setToast({ message: 'No media selected', type: 'error' }); return; }
        const ok = await saveMediaMeta(target, data.tags, data.description);
        if (ok) {
          setMeta(prev => ({ ...prev, [target]: { tags: data.tags, description: data.description } }));
          setToast({ message: 'Saved metadata', type: 'success' });
        } else {
          setToast({ message: 'Failed to save metadata', type: 'error' });
        }
        setMetaModalOpen(false);
        setPendingMetaFor(null);
      }}
      imageUrl={(pendingMetaFor?.url || selected?.url) ? `${(pendingMetaFor?.url || selected?.url)!.startsWith('http') ? '' : window.location.origin}${pendingMetaFor?.url || selected?.url}` : undefined}
    />
    <ImageLightbox
      open={lightboxOpen}
      items={lightboxItems}
      index={lightboxIndex}
      onClose={() => setLightboxOpen(false)}
      onChangeIndex={(i) => setLightboxIndex(i)}
    />
    <ComponentSettingsModal
      open={!!settingsOpen}
      onClose={() => { setSettingsOpen(false); setSettingsUrl(null); setSettingsInitial(null); }}
      componentUrl={settingsUrl || ''}
      initialProps={settingsInitial || undefined}
      prefabMode={!!settingsInitial}
    />
    {isJs(selected) && componentPreviewOpen && (
      <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50" onMouseDown={() => setComponentPreviewOpen(false)}>
        <div className="relative w-full max-w-5xl h-[85vh] bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden" onMouseDown={(e)=>e.stopPropagation()}>
          <div className="absolute top-3 right-3 flex gap-2">
            <button
              className="inline-flex h-9 px-3 items-center justify-center rounded-md bg-white/90 backdrop-blur border border-gray-200 shadow-sm hover:bg-white transition text-sm"
              onClick={() => setComponentPreviewOpen(false)}
            >
              Close
            </button>
          </div>
          <iframe
            title="Component Fullscreen Preview"
            className="w-full h-full bg-white"
            sandbox="allow-scripts allow-modals"
            srcDoc={`<!doctype html><html><head><meta charset=\"utf-8\"/><meta name=\"viewport\" content=\"width=device-width,initial-scale=1\"/><style>html,body{height:100%;margin:0;padding:0;background:#fff;font-family:ui-sans-serif,system-ui,-apple-system,Segoe UI,Roboto,Ubuntu,Cantarell,Noto Sans,sans-serif}#app{display:flex;align-items:center;justify-content:center;min-height:100vh;padding:24px}</style></head><body><div id=\"app\"></div><script>window.__COMPONENT_TARGET__=document.getElementById('app');</script><script src=\"${((selected?.url)||'').replace(/"/g,'&quot;')}\"></script></body></html>`}
          />
        </div>
      </div>
    )}
    </>
  );
};

export default MediaGallery;
